import nodeManagerAbi from "../assets/abis/node_manager.json";
import { getContract } from "../utils/contract";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useMemo } from "react";
import tiers from "../utils/tiers";

import { getNodeChecksum, useMasterOfCoinContract } from './masterOfCoin';
import { dateDiffInDays } from "../utils/utils";
import { BIFROST_LIBRARY, fees, tierFees, times, useBifrostLibrary } from "./bitfrostLibrary";



const useNodeManagerContract = (address) => {
  const web3context = useSelector((state) => state.web3.web3context);
  const ethereum = web3context?.library;

  return [
    ethereum,
    useMemo(
      () => getContract(nodeManagerAbi, address, ethereum),
      [ethereum, address]
    ),
  ];
};

export const useGetNodeRewards = (tier, createdAt) => {
  const [provider, contract] = useNodeManagerContract(tiers[tier].manager);
  const web3context = useSelector((state) => state.web3.web3context);
  const account = web3context?.account;
  
  async function getTotalRewards() {
    try {
      return contract.methods._getRewardAmountOf(account, createdAt).call();
    } catch (e) {
      console.log(e);
      return 0;
    }
  }

  return useQuery(
    ["nodeRewards", account, tier, createdAt, Boolean(provider)],
    getTotalRewards,
    {
      refetchInterval: 1000,
      enabled: Boolean(account && provider),
      cacheTime: 0,
    }
  );
};


export const useGetNodes = () => {
const web3context = useSelector((state) => state.web3.web3context);
  const ethereum = web3context?.library;
  const account = web3context?.account;
  
  

  async function getNodes() {
    try {
      const currentTiers = Object.values(tiers);
      const result = [];

      for (let index = 0; index < currentTiers.length; index++) {
        const { address, name } = currentTiers[index];
        let creationTimes = "";
        let lastClaimTimes = "";
        let nodeNames = "";

        try {
          const contract = getContract(nodeManagerAbi, address, ethereum);
          creationTimes = await contract.methods
            ._getNodesCreationTime(account)
            .call();
          lastClaimTimes = await contract.methods
            ._getNodesLastClaimTime(account)
            .call();
          nodeNames = await contract.methods._getNodesNames(account).call();

          const [times, names, lastClaim] = [
            creationTimes.split("#"),
            nodeNames.split("#"),
            lastClaimTimes.split("#"),
          ];

          for (let i = 0; i < times.length; i++) {
            result.push({
              name: names[i],
              createdAt: times[i],
              lastClaimTime: lastClaim[i],
              tier: name,
              id: `${times[i]}-${name}-${i}`,
            });
          }
        } catch (e) {}
      }

      return result;
    } catch (e) {
      console.log(e);
    }
  }

  return useQuery(["nodes", account, Boolean(ethereum)], getNodes, {
    refetchInterval: 10000,
    enabled: Boolean(account && ethereum),
    cacheTime: 0,
  });
};



export const useGetNodesV2 = () => {
    const web3context = useSelector((state) => state.web3.web3context);
    const ethereum = web3context?.library;
    const account = web3context?.account;

    const [__, mocContract] = useMasterOfCoinContract();
    const [_, bflContract] = useBifrostLibrary(BIFROST_LIBRARY);
    
    // 6 + Days will be considered “Good Standing” with green marker. 
    // 5 days left to pay node fees - “Due Soon” with Yellow Marker. 
    // 1 Day Left to pay nodes fees “Overdue” with red marker. 
    // When node fee passes due date it is considered “Inactive” and greyed out
    

    async function getDueDate(nodeId) {
      try {
        return mocContract.methods.getDueDate(nodeId).call();
      } catch (e) {
        console.log(e);
      }
    }

    async function getFee(lastClaimTime, tier) {
      try {
        const diff = Date.now() / 1000 - Number(lastClaimTime);
        return bflContract.methods
          .getCashOutTaxFeeWithLastClaimTime(
            String(Number(diff).toFixed(0)),
            times,
            fees,
            tierFees[tier]
          )
          .call();
      } catch (e) {
        console.log(e);
        return 0;
      }
    }

    
    const  getStatus = async (name, createdAt, tier) => {
      const nodeId = getNodeChecksum(
        name,
        createdAt,
        tier,
        String(account)
      );

      const dueDate = await getDueDate(nodeId)
      
      const _dueDate = new Date(Number(dueDate) * 1000);
      const _currentDate = new Date();
      const daysRemaining = dateDiffInDays(_dueDate, _currentDate);

      let status = {'status': 'INACTIVE', 'table_status': 'INACTIVE', 'color': '#505563'}
      if(daysRemaining >= 6){
        status = {'status': 'GOOD STANDING','table_status':`${daysRemaining} DAYS LEFT`, 'color': '#32B267'};
      }
      else if(daysRemaining > 1){
        status = {'status':'DUE SOON', 'table_status':`${daysRemaining} DAYS LEFT`, 'color': '#dfab43' };
      }
      else if(daysRemaining === 1 || daysRemaining === 0){
        status = {'status': 'OVERDUE', 'table_status':'OVERDUE', 'color': '#de3f44'};
      }

      return {status, daysRemaining};
    }


    async function getNodes() {
      try {
        const currentTiers = Object.values(tiers);
        const result = [];
  
        for (let index = 0; index < currentTiers.length; index++) {
          const { manager: address, name } = currentTiers[index];
          let creationTimes = "";
          let lastClaimTimes = "";
          let nodeNames = "";
  
          try {
            const contract = getContract(nodeManagerAbi, address, ethereum);
            creationTimes = await contract.methods
              ._getNodesCreationTime(account)
              .call();
            lastClaimTimes = await contract.methods
              ._getNodesLastClaimTime(account)
              .call();
            nodeNames = await contract.methods._getNodesNames(account).call();
  
            const [times, names, lastClaim] = [
              creationTimes.split("#"),
              nodeNames.split("#"),
              lastClaimTimes.split("#"),
            ];
  
            for (let i = 0; i < times.length; i++) {
              const {status, daysRemaining: daysLeft} = await getStatus(names[i], times[i], name);
              const obj = {
                name: names[i],
                createdAt: times[i],
                lastClaimTime: Number(lastClaim[i]),
                tier: name,
                tier_image: tiers[name].image,
                status,
                daysLeft: daysLeft,
                tax: await getFee(Number(lastClaim[i]), name),
                id: `${times[i]}-${name}-${i}`,
              }
              result.push(obj)
            }
          } catch (e) {}
        }
  
        return result;
      } catch (e) {
        console.log(e);
      }
    }
  
    return useQuery(["nodes", account, Boolean(ethereum)], getNodes, {
      refetchInterval: 10000,
      enabled: Boolean(account && ethereum),
      cacheTime: 0,
    });
  };
  